import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../../contexts/AuthContext';
import GuideButton from '../../../common/guide/GuideButton';
import { emptyLotSelection, type LotSelection } from '../../../accounting/inventory/types/lots.types';
import ProductionLotDialog from '../orders/ProductionLotDialog';
import { formatLotSelection } from '../../utils/productionLots';
import { accountingApi } from '../../../../config/apiClient';
import { ACCOUNTING_API } from '../../../../config/api';
import InventoryProductSelectionModal from '../../../accounting/inventory/components/InventoryProductSelectionModal';
import type { InventoryCategory, InventoryItem, Warehouse } from '../../../accounting/inventory/types/inventory.types';
import EntryUnitSelect from '../shared/EntryUnitSelect';
import ShortageDialog from '../../dialogs/ShortageDialog';
import { useItemUnitOptions } from '../../hooks/useItemUnitOptions';
import { getWarehouses, ProductionApiError } from '../../services/productionService';
import { confirmStep, getConsumptionProposal, getOrderSteps } from '../../services/productionOpsService';
import type {
  ProductionOrderStep,
  ProductionStockShortage,
  ProductionWarehouse,
  StepConsumptionProposalLine,
} from '../../types/production.types';
import { entryLineQuantities, formatQty, toBaseQty, toDisplayQty } from '../../utils/productionFormat';
import CompleteOperationsDialog from './CompleteOperationsDialog';
import OperatorPicker, { readStoredOperator, type OperatorValue } from './OperatorPicker';
import QualityCheckSection from './QualityCheckSection';
import { blankEntry, buildCheckResults, type QualityEntry } from './qualityEntry';

interface ConsumptionEditRow {
  key: string;
  productId: string;
  productName: string;
  stockCode?: string | null;
  unit: string;
  entryUnit: string | null;
  entryFactor: number | null;
  quantity: number;
  warehouseId: string;
  warehouseName?: string | null;
  available: number | null;
  isShort: boolean;
  canBeNegative: boolean;
  manual: boolean;
  trackLots?: boolean;
  lotSelection?: LotSelection;
  lotNoById?: Record<string, string>;
  proposedLots?: { lotNo?: string | null; quantity: number; expired?: boolean }[];
  lotShortage?: number | null;
}

interface ConfirmStepDialogProps {
  open: boolean;
  step: ProductionOrderStep | null;
  orderId: string;
  mode: 'desk' | 'touch';
  productName: string;
  stockCode?: string | null;
  unit: string;
  outputWarehouseName?: string | null;
  onClose: () => void;
  onDone: (warnings?: string[]) => void;
}

function parseDecimal(value: string): number {
  const parsed = Number(value.trim().replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : Number.NaN;
}

function fromProposal(line: StepConsumptionProposalLine): ConsumptionEditRow {
  return {
    key: line.productId,
    productId: line.productId,
    productName: line.productName,
    stockCode: line.stockCode,
    unit: line.unit,
    entryUnit: line.entryUnit ?? null,
    entryFactor: line.entryFactor ?? null,
    quantity: line.quantity,
    warehouseId: line.warehouseId,
    warehouseName: line.warehouseName,
    available: line.available,
    isShort: line.isShort,
    canBeNegative: line.canBeNegative,
    manual: false,
    trackLots: line.trackLots,
    lotSelection: emptyLotSelection(),
    proposedLots: (line.proposedLots ?? []).map((lot) => ({ lotNo: lot.lotNo, quantity: lot.quantity, expired: lot.expired })),
    lotShortage: line.lotShortage ?? null,
  };
}

const ConfirmStepDialog: React.FC<ConfirmStepDialogProps> = ({
  open,
  step,
  orderId,
  mode,
  productName,
  stockCode,
  unit,
  outputWarehouseName,
  onClose,
  onDone,
}) => {
  const { t } = useTranslation('production');
  const { isModuleEnabled } = useAuth();
  const lotsEnabled = isModuleEnabled('lot_tracking');
  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const requestId = useRef('');
  const [good, setGood] = useState('');
  const [scrap, setScrap] = useState('0');
  const [finish, setFinish] = useState(false);
  const [setup, setSetup] = useState('');
  const [run, setRun] = useState('');
  const [notes, setNotes] = useState('');
  const [timeOpen, setTimeOpen] = useState(false);
  const [consumptionOpen, setConsumptionOpen] = useState(true);
  const [rows, setRows] = useState<ConsumptionEditRow[]>([]);
  const [operator, setOperator] = useState<OperatorValue>({ uid: null, name: '' });
  const [warehouses, setWarehouses] = useState<ProductionWarehouse[]>([]);
  const [categories, setCategories] = useState<InventoryCategory[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [proposalPending, setProposalPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [shortages, setShortages] = useState<ProductionStockShortage[]>([]);
  const [shortageOpen, setShortageOpen] = useState(false);
  const [shortageDetail, setShortageDetail] = useState<string | undefined>();
  const [lotKey, setLotKey] = useState<string | null>(null);
  const [askComplete, setAskComplete] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [produced, setProduced] = useState(0);
  const [quality, setQuality] = useState<Record<string, QualityEntry>>({});
  const proposalToken = useRef(0);

  const touch = mode === 'touch';
  const max = step?.maxConfirmable ?? null;
  const goodN = parseDecimal(good);
  const scrapN = parseDecimal(scrap);
  const overMax = max != null && Number.isFinite(goodN) && Number.isFinite(scrapN) && goodN + scrapN > max;

  const stepId = step?.id ?? '';
  const openKey = open && stepId ? stepId : '';
  const [openKeySeen, setOpenKeySeen] = useState('');
  if (openKey !== openKeySeen) {
    setOpenKeySeen(openKey);
    if (openKey) {
      requestId.current = crypto.randomUUID();
      setGood('');
      setScrap('0');
      setFinish(false);
      setSetup('');
      setRun('');
      setNotes('');
      setTimeOpen(false);
      setConsumptionOpen(mode !== 'touch');
      setRows([]);
      setOperator(readStoredOperator());
      setQuality(Object.fromEntries((step?.checks ?? []).map((check) => [check.id, blankEntry()])));
      setError(null);
      setWarnings([]);
      setAskComplete(false);
      setCompleteOpen(false);
      setProposalPending(false);
    }
  }

  useEffect(() => {
    if (!openKey) return undefined;
    let active = true;
    getWarehouses()
      .then((list) => {
        if (active) setWarehouses(list);
      })
      .catch(() => {
        if (active) setWarehouses([]);
      });
    return () => {
      active = false;
    };
  }, [openKey]);

  useEffect(() => {
    if (max == null || !Number.isFinite(goodN) || !Number.isFinite(scrapN)) return;
    if (goodN + scrapN >= max && goodN + scrapN > 0) setFinish(true);
  }, [goodN, scrapN, max]);

  useEffect(() => {
    if (!open || !stepId || !Number.isFinite(goodN) || !Number.isFinite(scrapN) || goodN < 0 || scrapN < 0) {
      setProposalPending(false);
      return undefined;
    }
    setProposalPending(true);
    const token = ++proposalToken.current;
    const timer = window.setTimeout(() => {
      getConsumptionProposal(stepId, goodN, scrapN)
        .then((proposal) => {
          if (token !== proposalToken.current) return;
          setRows((current) => {
            const manual = current.filter((row) => row.manual);
            const manualIds = new Set(manual.map((row) => row.productId));
            const proposed = (proposal.lines ?? [])
              .filter((line) => !manualIds.has(line.productId))
              .map(fromProposal);
            return [...proposed, ...manual];
          });
        })
        .catch(() => undefined)
        .finally(() => {
          if (token === proposalToken.current) setProposalPending(false);
        });
    }, 400);
    return () => window.clearTimeout(timer);
  }, [open, stepId, goodN, scrapN]);

  const unitLookup = useMemo(() => ({
    ids: rows.map((row) => row.productId),
    bases: Object.fromEntries(rows.map((row) => [row.productId, row.unit || ''])),
  }), [rows]);
  const unitsByItem = useItemUnitOptions(unitLookup.ids, unitLookup.bases);

  const defaultWarehouse = warehouses.find((warehouse) => warehouse.isDefault)?.id || warehouses[0]?.id || '';
  const modalWarehouses: Warehouse[] = warehouses.map((warehouse) => ({
    id: warehouse.id,
    name: warehouse.name,
    isDefault: warehouse.isDefault,
    branchesId: warehouse.branchId,
  }));

  const mark = (key: string, patch: Partial<ConsumptionEditRow>) => {
    setRows((current) => current.map((row) => {
      if (row.key !== key) return row;
      const next = { ...row, ...patch, manual: true };
      const available = next.available;
      next.isShort = available != null && next.quantity > available;
      return next;
    }));
  };

  const bump = (delta: number) => {
    const base = Number.isFinite(goodN) ? goodN : 0;
    const next = Math.max(0, Math.round((base + delta) * 1000) / 1000);
    setGood(String(next));
  };

  const openPicker = () => {
    if (categories.length > 0) {
      setPickerOpen(true);
      return;
    }
    accountingApi.get(ACCOUNTING_API.INVENTORY_CATEGORIES)
      .then((response) => {
        const data = response.data as InventoryCategory[] | { data?: InventoryCategory[] };
        setCategories(Array.isArray(data) ? data : (data?.data ?? []));
      })
      .catch(() => setCategories([]))
      .finally(() => setPickerOpen(true));
  };

  const addMaterials = (items: InventoryItem[]) => {
    setRows((current) => [
      ...current,
      ...items.filter((item) => !current.some((row) => row.productId === item.id)).map((item) => ({
        key: crypto.randomUUID(),
        productId: item.id,
        productName: item.name,
        stockCode: item.stockCode,
        unit: item.unit,
        entryUnit: null,
        entryFactor: null,
        quantity: 1,
        warehouseId: defaultWarehouse,
        available: null,
        isShort: false,
        canBeNegative: item.canBeNegative ?? false,
        manual: true,
        trackLots: item.trackLots,
        lotSelection: emptyLotSelection(),
      })),
    ]);
    setPickerOpen(false);
  };

  const plannedRun = step && Number.isFinite(goodN) && Number.isFinite(scrapN)
    ? step.runMinutesPerUnit * (goodN + scrapN)
    : step?.runMinutesPerUnit;

  const save = async () => {
    if (!step || busy) return;
    if (!Number.isFinite(goodN) || goodN < 0 || good.trim() === '' || !Number.isFinite(scrapN) || scrapN < 0 || goodN + scrapN <= 0) {
      setError(t('editor.validation'));
      return;
    }
    if ((setup.trim() !== '' && !Number.isFinite(parseDecimal(setup))) || (run.trim() !== '' && !Number.isFinite(parseDecimal(run)))) {
      setError(t('editor.validation'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await confirmStep(step.id, {
        GoodQuantity: goodN,
        ScrapQuantity: scrapN,
        SetupMinutes: setup.trim() === '' ? null : parseDecimal(setup),
        RunMinutes: run.trim() === '' ? null : parseDecimal(run),
        OperatorUid: operator.uid,
        OperatorName: operator.name.trim() || null,
        Notes: notes.trim() || null,
        FinishStep: finish,
        ClientRequestId: requestId.current,
        CheckResults: buildCheckResults(step.checks ?? [], quality),
        Consumptions: rows.map((row) => {
          // Dokunulmamış öneri satırı ana birimde gider: giriş birimine çevirip geri almak 1 -> 0,333333 -> 0,999999 kaydırır.
          if (!row.manual) {
            return {
              ProductId: row.productId,
              Quantity: row.quantity,
              EntryUnit: null,
              WarehouseId: row.warehouseId || null,
              Lots: !touch && row.trackLots && row.lotSelection?.mode === 'manual' && row.lotSelection.lots.length
                ? row.lotSelection.lots.map((lot) => ({ LotId: lot.lotId, Quantity: lot.quantity }))
                : null,
            };
          }
          const quoted = entryLineQuantities({
            unit: row.unit,
            entryUnit: row.entryUnit,
            entryFactor: row.entryFactor,
            plannedQuantity: row.quantity,
            actualQuantity: row.quantity,
          });
          return {
            ProductId: row.productId,
            Quantity: quoted.ActualQuantity,
            EntryUnit: quoted.EntryUnit,
            WarehouseId: row.warehouseId || null,
            Lots: !touch && row.trackLots && row.lotSelection?.mode === 'manual' && row.lotSelection.lots.length
              ? row.lotSelection.lots.map((lot) => ({ LotId: lot.lotId, Quantity: lot.quantity }))
              : null,
          };
        }),
      });
      const nextWarnings = result.warnings ?? [];
      setWarnings(nextWarnings);
      onDone(nextWarnings);
      if (finish) {
        try {
          const loaded = await getOrderSteps(orderId);
          if (loaded.canComplete) {
            const last = [...loaded.steps].sort((left, right) => left.stepNo - right.stepNo).pop();
            setProduced(last?.goodQuantity ?? 0);
            setAskComplete(true);
            return;
          }
        } catch {
          onClose();
          return;
        }
      }
      onClose();
    } catch (caught) {
      if (caught instanceof ProductionApiError && (
        caught.code === 'INSUFFICIENT_STOCK'
        || caught.code === 'LOT_INSUFFICIENT'
        || caught.code === 'LOT_ON_HOLD'
        || caught.code === 'LOT_EXPIRED'
      )) {
        setShortages(caught.shortages ?? []);
        setShortageDetail(caught.message);
        setShortageOpen(true);
      } else if (caught instanceof ProductionApiError && caught.code === 'INVALID_STATUS') {
        setError(caught.message);
        onDone();
      } else {
        setError(caught instanceof ProductionApiError ? caught.message : t('errors.VALIDATION_ERROR'));
      }
    } finally {
      setBusy(false);
    }
  };

  const runPlaceholder = plannedRun == null ? '' : String(Math.round(plannedRun * 100) / 100);

  return (
    <>
      <Dialog
        open={open && step != null}
        onClose={busy ? undefined : onClose}
        fullWidth
        maxWidth="md"
        fullScreen={touch && smallScreen}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, ...(touch ? { fontSize: '1.4rem' } : {}) }}>
          <Box component="span" sx={{ flex: 1 }}>
            {t('ops.confirmTitle')}
            {step ? ` · ${step.stepNo} ${step.name}` : ''}
          </Box>
          <GuideButton guideKey="production.confirmStep" />
        </DialogTitle>
        <DialogContent>
          {error ? <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert> : null}
          {warnings.length > 0 ? <Alert severity="warning" sx={{ mb: 1 }}>{warnings.join(' ')}</Alert> : null}
          {overMax && max != null ? <Alert severity="warning" sx={{ mb: 1 }}>{t('ops.overMax', { count: max })}</Alert> : null}
          <Stack direction={touch ? 'column' : 'row'} spacing={1} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              required
              label={t('ops.good')}
              value={good}
              onChange={(event) => setGood(event.target.value)}
              inputProps={{ inputMode: 'decimal', style: touch ? { fontSize: '1.6rem' } : undefined }}
              helperText={max != null ? t('ops.maxConfirmable', { count: max }) : ' '}
              error={overMax}
            />
            <TextField
              fullWidth
              label={t('ops.scrap')}
              value={scrap}
              onChange={(event) => setScrap(event.target.value)}
              inputProps={{ inputMode: 'decimal', style: touch ? { fontSize: '1.6rem' } : undefined }}
            />
          </Stack>
          {touch ? (
            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
              <Button variant="outlined" onClick={() => bump(1)}>+1</Button>
              <Button variant="outlined" onClick={() => bump(10)}>+10</Button>
              <Button variant="outlined" onClick={() => bump(-1)}>-1</Button>
            </Stack>
          ) : null}
          <FormControlLabel
            control={<Checkbox checked={finish} onChange={(event) => setFinish(event.target.checked)} />}
            label={t('ops.finishStep')}
          />
          <QualityCheckSection
            checks={step?.checks ?? []}
            touch={touch}
            finish={finish}
            value={quality}
            onChange={setQuality}
          />
          <Box sx={{ mb: 1 }}>
            <Button size="small" onClick={() => setTimeOpen((current) => !current)}>{t('ops.timeSection')}</Button>
            <Collapse in={timeOpen}>
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  label={t('ops.setupMinutes')}
                  value={setup}
                  placeholder={step ? String(step.setupMinutes) : ''}
                  onChange={(event) => setSetup(event.target.value)}
                  inputProps={{ inputMode: 'decimal' }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label={t('ops.runMinutes')}
                  value={run}
                  placeholder={runPlaceholder}
                  onChange={(event) => setRun(event.target.value)}
                  inputProps={{ inputMode: 'decimal' }}
                />
              </Stack>
            </Collapse>
          </Box>
          <Box sx={{ mb: 1 }}>
            <Button size="small" onClick={() => setConsumptionOpen((current) => !current)}>{t('ops.proposal')}</Button>
            <Collapse in={consumptionOpen}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', my: 1 }}>
                <Button size="small" variant="outlined" onClick={openPicker}>{t('ops.addMaterial')}</Button>
              </Box>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('editor.columns.material')}</TableCell>
                    <TableCell>{t('editor.columns.unit')}</TableCell>
                    <TableCell align="right">{t('editor.quantity')}</TableCell>
                    <TableCell>{t('editor.columns.warehouse')}</TableCell>
                    {lotsEnabled ? <TableCell>{t('lots.column')}</TableCell> : null}
                    <TableCell align="right">{t('editor.columns.available')}</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => {
                    const blocked = row.isShort && !row.canBeNegative;
                    return (
                      <TableRow key={row.key} sx={blocked ? { bgcolor: (mui) => alpha(mui.palette.error.light, 0.35) } : undefined}>
                        <TableCell>
                          <Typography variant="body2">{row.productName}</Typography>
                          {row.manual ? <Typography variant="caption">{t('ops.manual')}</Typography> : null}
                        </TableCell>
                        <TableCell>
                          <EntryUnitSelect
                            baseUnit={row.unit}
                            options={unitsByItem[row.productId]?.options ?? []}
                            value={row.entryUnit}
                            factor={row.entryFactor}
                            plainWhenSingle={false}
                            onChange={(entryUnit, entryFactor) => mark(row.key, { entryUnit, entryFactor })}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            size="small"
                            value={toDisplayQty(row.quantity, row.entryFactor)}
                            onChange={(event) => {
                              const next = Number(event.target.value);
                              if (Number.isFinite(next)) mark(row.key, { quantity: toBaseQty(next, row.entryFactor) });
                            }}
                            inputProps={{ inputMode: 'decimal', style: { textAlign: 'right', width: 88 } }}
                          />
                        </TableCell>
                        <TableCell>
                          <FormControl size="small" fullWidth>
                            <Select
                              value={row.warehouseId}
                              onChange={(event) => mark(row.key, { warehouseId: event.target.value })}
                            >
                              {warehouses.map((warehouse) => (
                                <MenuItem key={warehouse.id} value={warehouse.id}>{warehouse.name}</MenuItem>
                              ))}
                              {!warehouses.some((warehouse) => warehouse.id === row.warehouseId) && row.warehouseId ? (
                                <MenuItem value={row.warehouseId}>{row.warehouseName || row.warehouseId}</MenuItem>
                              ) : null}
                            </Select>
                          </FormControl>
                        </TableCell>
                        {lotsEnabled ? (
                          <TableCell>
                            {row.trackLots ? (
                              touch ? (
                                <Typography variant="caption">{t('lots.fefoTouch')}</Typography>
                              ) : (
                                <Button size="small" onClick={() => setLotKey(row.key)}>
                                  {formatLotSelection(row.lotSelection, row.lotNoById, t('lots.fefo'))}
                                </Button>
                              )
                            ) : '—'}
                          </TableCell>
                        ) : null}
                        <TableCell align="right" sx={{ color: blocked ? 'error.main' : undefined }}>
                          {row.available == null ? '—' : formatQty(toDisplayQty(row.available, row.entryFactor))}
                          {blocked ? ` · ${t('ops.short')}` : ''}
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" aria-label={t('editor.deleteLine')} onClick={() => setRows((current) => current.filter((item) => item.key !== row.key))}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Collapse>
          </Box>
          <Box sx={{ mt: 1 }}>
            <OperatorPicker value={operator} onChange={setOperator} />
          </Box>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            label={t('ops.notes')}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={busy}>{t('ops.cancel')}</Button>
          <Button variant="contained" onClick={save} disabled={busy || proposalPending || (max != null && max <= 0)}>{t('ops.save')}</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={askComplete} onClose={() => { setAskComplete(false); onClose(); }}>
        <DialogTitle>{t('ops.completeOperations')}</DialogTitle>
        <DialogContent>
          {warnings.length > 0 ? <Alert severity="warning" sx={{ mb: 1 }}>{warnings.join(' ')}</Alert> : null}
          <Typography>{t('ops.allDoneAskComplete')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAskComplete(false); onClose(); }}>{t('ops.no')}</Button>
          <Button variant="contained" onClick={() => { setAskComplete(false); setCompleteOpen(true); }}>{t('ops.yes')}</Button>
        </DialogActions>
      </Dialog>
      <CompleteOperationsDialog
        open={completeOpen}
        orderId={orderId}
        producedQuantity={produced}
        productName={productName}
        stockCode={stockCode}
        unit={unit}
        warehouseName={outputWarehouseName}
        onClose={() => { setCompleteOpen(false); onClose(); }}
        onDone={onDone}
      />
      <ProductionLotDialog
        open={lotKey != null}
        title={rows.find((row) => row.key === lotKey)?.productName || t('lots.column')}
        itemId={rows.find((row) => row.key === lotKey)?.productId ?? ''}
        warehouseId={rows.find((row) => row.key === lotKey)?.warehouseId ?? ''}
        quantity={rows.find((row) => row.key === lotKey)?.quantity ?? 0}
        value={rows.find((row) => row.key === lotKey)?.lotSelection ?? emptyLotSelection()}
        onClose={() => setLotKey(null)}
        onApply={(selection, names) => {
          if (lotKey) mark(lotKey, { lotSelection: selection, lotNoById: names });
          setLotKey(null);
        }}
      />
      <InventoryProductSelectionModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        title={t('editor.materialTitle')}
        warehouses={modalWarehouses}
        categories={categories}
        excludeItemIds={rows.map((row) => row.productId)}
        excludeMenuItems
        onSelectProducts={addMaterials}
      />
      <ShortageDialog
        open={shortageOpen}
        rows={shortages}
        detail={shortageDetail}
        onClose={() => setShortageOpen(false)}
      />
    </>
  );
};

export default ConfirmStepDialog;
